import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { motion } from "motion/react";
import { ArrowRight, ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Sector } from "recharts";
import { KPI_DATA, PIPELINE_STAGES } from "./grcDashboardData";
import type { PipelineStageItem } from "./grcDashboardData";

/* ── Animated width bar (left-to-right) ────────────────────────────── */
export function AnimatedBar({
  pct,
  color,
  delay = 0,
  height = "h-2",
}: {
  pct: number;
  color: string;
  delay?: number;
  height?: string;
}) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setWidth(pct), delay + 120);
    return () => clearTimeout(t);
  }, [pct, delay]);

  return (
    <div className={`relative w-full overflow-hidden rounded-full bg-foreground/[0.06] ${height}`}>
      <div
        className="absolute inset-y-0 left-0 rounded-full transition-all duration-[1100ms] ease-out"
        style={{ width: `${width}%`, background: color }}
      />
    </div>
  );
}

/* ── Configure-widgets message — shown (centered) when the user's role has no
   dashboard widgets enabled. Nothing else renders; an admin must turn widgets
   on for the role before any cards/KPIs appear. ──────────────────────────── */
export function ConfigureWidgetsMessage() {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-border px-6 text-center">
      <div className="flex size-14 items-center justify-center rounded-2xl bg-indigo-500/10 text-indigo-500 dark:text-indigo-400">
        <SlidersHorizontal className="size-7" />
      </div>
      <div className="space-y-1.5">
        <p className="text-base font-semibold text-foreground">Set Up Your Dashboard Insights</p>
        <p className="mx-auto max-w-sm text-sm text-muted-foreground">
          No insight cards are enabled yet. Open the Insights Configuration page to
          choose which cards and charts appear on the dashboard for each role.
        </p>
      </div>
      <button
        type="button"
        onClick={() => navigate("/admin/dashboard-config")}
        className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
      >
        Go to Insights Configuration
        <ArrowRight className="size-3.5" />
      </button>
    </div>
  );
}

/* ── Empty state — shown inside a card when there is no data ────────── */
export function NoData({ label = "No Data Available" }: { label?: string }) {
  return (
    <div className="flex h-full min-h-[160px] w-full items-center justify-center py-10">
      <span className="text-sm font-bold text-[#1e3a8a] dark:text-blue-300">{label}</span>
    </div>
  );
}

/* ── Donut tooltip ──────────────────────────────────────────────────── */
export function DonutTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number }>;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-foreground/10 bg-card/95 px-3 py-2 text-xs shadow-xl">
      <span className="font-semibold text-foreground">{payload[0].name}</span>
      <span className="ml-2 text-blue-400">{payload[0].value}%</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   SHARED DONUT PANEL — used by every GRC donut (group + org dashboards)
   Legend on top, elbow leader-line % labels, centered total.
   ══════════════════════════════════════════════════════════════════════ */
export interface DonutPanelSlice {
  label: string;
  value: number;
  pct: number;
  color: string;
}


const LEGEND_PER_PAGE = 3;

export function DonutPanel({
  slices,
  centerValue,
  centerLabel,
  legendPerPage = LEGEND_PER_PAGE,
}: {
  slices: DonutPanelSlice[];
  centerValue: number | string;
  centerLabel: string;
 
  legendPerPage?: number;
}) {
  /* ── Clickable legend → toggle a segment on/off ── */
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const toggleSlice = (label: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next.size >= slices.length ? prev : next; // never hide every segment
    });

  const activeSlices = slices.filter((s) => !hidden.has(s.label));
  /* keep every slice in the data (hidden → value 0) so recharts tweens the
     sector shrinking/growing instead of hard-redrawing the whole donut */
  const chartData = slices.map((s) => ({
    name: s.label,
    value: hidden.has(s.label) ? 0 : s.value,
    pct: s.pct,
    color: s.color,
  }));

  /* recompute the centre total only for count-based donuts (where the passed
     total equals the sum of all slice values) */
  const allTotal = slices.reduce((a, s) => a + s.value, 0);
  const activeTotal = activeSlices.reduce((a, s) => a + s.value, 0);
  const displayCenter =
    typeof centerValue === "number" && centerValue === allTotal ? activeTotal : centerValue;

  /* hovered slice pops out (like the reference active-shape).
     -1 = nothing hovered (explicit "none" so recharts doesn't fall back to its
     tooltip-driven active state and leave the glow stuck on after hover-out). */
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const [legendPage, setLegendPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(slices.length / LEGEND_PER_PAGE));
  const page = Math.min(legendPage, totalPages - 1);
  const pageItems = slices.slice(page * LEGEND_PER_PAGE, page * LEGEND_PER_PAGE + LEGEND_PER_PAGE);

  return (
    <div className="flex h-full w-full flex-col items-center">
      {/* legend — labels on the left (clip if too wide), pager pinned on the
          right as a SEPARATE sibling so it can never be clipped/overlapped */}
      <div className="mb-1 flex w-full items-center gap-2">
        <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-x-4 overflow-hidden">
          {pageItems.map((s) => {
            const isHidden = hidden.has(s.label);
            return (
              <button
                key={s.label}
                type="button"
                onClick={() => toggleSlice(s.label)}
                title={isHidden ? `Show ${s.label}` : `Hide ${s.label}`}
                className={`flex shrink-0 items-center gap-1.5 transition-opacity ${isHidden ? "opacity-40" : "hover:opacity-75"}`}
              >
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: isHidden ? "#94a3b8" : s.color }}
                />
                <span className="whitespace-nowrap text-sm font-medium text-foreground/85">{s.label}</span>
              </button>
            );
          })}
        </div>

        {totalPages > 1 && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setLegendPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30"
              aria-label="Previous legend page"
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="text-xs font-semibold tabular-nums text-muted-foreground">
              {page + 1}/{totalPages}
            </span>
            <button
              type="button"
              onClick={() => setLegendPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              className="flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30"
              aria-label="Next legend page"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        )}
      </div>

      {/* donut — clearing active state on leave avoids a stuck tooltip/highlight.
          flex-1 + min-height lets it fill (and resize with) a flexible card while
          never collapsing inside a fixed-height card. */}
      <div
        className="relative w-full flex-1 [&_*:focus]:outline-none [&_svg]:outline-none"
        style={{ minHeight: 220 }}
        onMouseLeave={() => {
          setActiveIndex(-1);
          setIsTooltipVisible(false);
        }}
        onMouseMove={(e) => {
          // Only track hover if mouse is actually over the chart area (center-ish coordinates)
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          const distFromCenter = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
          
          // Only allow hover interactions within donut radius (radius ~90px + margins)
          if (distFromCenter > 140) {
            setActiveIndex(-1);
            setIsTooltipVisible(false);
          }
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart
            margin={{ top: 26, right: 56, bottom: 26, left: 56 }}
            onMouseLeave={() => {
              setActiveIndex(-1);
              setIsTooltipVisible(false);
            }}
          >
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={62}
              outerRadius={90}
              paddingAngle={0}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              labelLine={false}
              label={renderLeaderLabel}
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              onMouseEnter={(_, idx) => {
                setActiveIndex(idx);
                setIsTooltipVisible(true);
              }}
              onMouseLeave={() => {
                setActiveIndex(-1);
                setIsTooltipVisible(false);
              }}
              isAnimationActive
              animationDuration={650}
              animationEasing="ease-in-out"
            >
              {chartData.map((s, idx) => (
                <Cell key={s.name ?? idx} fill={s.color} stroke={s.color} strokeWidth={0} />
              ))}
            </Pie>
            <Tooltip 
              content={<SliceTooltip visible={isTooltipVisible} />} 
              isAnimationActive={false} 
              cursor={false}
              wrapperStyle={isTooltipVisible ? {} : { display: 'none' }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xs text-muted-foreground">{centerLabel}</span>
          <span className="mt-0.5 text-2xl font-bold leading-none text-foreground">{displayCenter}</span>
        </div>
      </div>
    </div>
  );
}

/* Recharts active-shape: hovered slice pops outward + soft glow */
function renderActiveShape(props: {
  cx: number; cy: number; innerRadius: number; outerRadius: number;
  startAngle: number; endAngle: number; fill: string;
}) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g style={{ filter: `drop-shadow(0 0 6px ${fill}99)` }}>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
}

/* Recharts custom label: elbow leader line + outside % text */
function renderLeaderLabel(props: {
  cx: number; cy: number; midAngle: number; outerRadius: number;
  percent: number; payload: { color: string };
}) {
  const { cx, cy, midAngle, outerRadius, percent, payload } = props;
  if (!percent) return null;
  const RADIAN = Math.PI / 180;
  const cos = Math.cos(-midAngle * RADIAN);
  const sin = Math.sin(-midAngle * RADIAN);
  const sx = cx + (outerRadius + 2) * cos;
  const sy = cy + (outerRadius + 2) * sin;
  const mx = cx + (outerRadius + 16) * cos;
  const my = cy + (outerRadius + 16) * sin;
  const dir = cos >= 0 ? 1 : -1;
  const ex = mx + dir * 18;
  const ey = my;
  const anchor = cos >= 0 ? "start" : "end";
  const color = payload.color;

  return (
    <g>
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={color} strokeWidth={1.2} fill="none" />
      <circle cx={ex} cy={ey} r={1.8} fill={color} />
      <text
        x={ex + dir * 4}
        y={ey}
        textAnchor={anchor}
        dominantBaseline="central"
        fontSize={13}
        fontWeight={700}
        fill="var(--foreground)"
      >
        {(percent * 100).toFixed(2)}%
      </text>
    </g>
  );
}

/* Donut tooltip — shows "value (pct%)", or just "pct%" when value === pct */
function SliceTooltip({
  active,
  visible = true,
  payload,
}: {
  active?: boolean;
  visible?: boolean;
  payload?: Array<{ name: string; value: number; payload: { pct: number } }>;
}) {
  if (!visible || !active || !payload?.length) return null;
  const p = payload[0];
  const pct = p.payload?.pct;
  return (
    <div className="whitespace-nowrap rounded-lg border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-2xl">
      <span className="font-semibold">{p.name}</span>
      <span className="ml-2 font-bold text-primary">
        {p.value === pct ? `${pct}%` : `${p.value} (${pct}%)`}
      </span>
    </div>
  );
}

/* â”€â”€ Animated counter hook â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export function useCountUp(target: number, duration = 1100, delay = 0) {
  const [count, setCount] = useState(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    startRef.current = null;
    let rafId: number;
    const timeout = setTimeout(() => {
      function step(ts: number) {
        if (startRef.current === null) startRef.current = ts;
        const progress = Math.min((ts - startRef.current) / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(ease * target));
        if (progress < 1) rafId = requestAnimationFrame(step);
      }
      rafId = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(rafId);
    };
  }, [target, duration, delay]);

  return count;
}

/* â”€â”€ Open findings color by severity â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export function findingColor(severity: string): string {
  const map: Record<string, string> = {
    critical: "#ef4444",
    high:     "#f97316",
    medium:   "#f59e0b",
    low:      "#64748b",
  };
  return map[severity] ?? map.low;
}

/* â”€â”€ Card wrapper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl border border-border bg-card p-5 pt-3 ${className}`}
    >
      {children}
    </div>
  );
}

/* â”€â”€ Section title â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export function SectionTitle({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {subtitle && (
        <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );
}

/* â”€â”€ KPI Card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export function KpiCard({
  kpi,
  index,
  loading = false,
}: {
  kpi: (typeof KPI_DATA)[0];
  index: number;
  loading?: boolean;
}) {
  const numericValue =
    typeof kpi.value === "number" ? kpi.value : parseInt(kpi.value as string);
  const isPercent =
    typeof kpi.value === "string" && (kpi.value as string).includes("%");
  /* 0 / blank / non-numeric ("—") → no data: show a centred message instead. */
  const noData = !numericValue || Number.isNaN(numericValue);
  const counted = useCountUp(numericValue, 1000, index * 80);
  const Icon = kpi.icon;

  return (
    <motion.div
      initial={{ opacity: 0, x: -24 }}
      animate={{
        opacity: 1,
        x: 0,
        transition: { duration: 0.4, delay: index * 0.07, ease: "easeOut" },
      }}
      transition={{ duration: 0.12, ease: "easeOut" }}
      whileHover={{
        y: -3,
        boxShadow: `0 8px 22px -12px ${kpi.color}55`,
        borderColor: `${kpi.color}40`,
      }}
      className={`group relative flex h-full flex-col overflow-hidden rounded-xl border ${kpi.borderColor} bg-card p-3`}
    >
      {/* left side accent (hidden in the Manage-dashboard selector via .kpi-accent) */}
      <span
        className="kpi-accent pointer-events-none absolute inset-y-0 left-0 w-1 transition-all duration-300 group-hover:w-1.5"
        style={{ background: kpi.color }}
      />

      {/* label + icon — pinned to the top, fixed height so every card aligns */}
      <div className="flex min-h-[2rem] items-start justify-between gap-2">
        <p className="whitespace-nowrap text-sm font-semibold leading-tight tracking-tight text-foreground">
          {kpi.label}
        </p>
        <div
          className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg transition-transform duration-300 group-hover:scale-110"
          style={{ background: `${kpi.color}18` }}
        >
          <Icon className="size-4" style={{ color: kpi.color }} />
        </div>
      </div>

      {/* value zone — always bottom-left so the whole row shares one baseline */}
      <div className="mt-auto flex flex-col gap-0.5 pt-1">
        {loading ? (
          <div className="h-7 w-16 animate-pulse rounded bg-foreground/[0.08]" />
        ) : noData ? (
          <>
            {/* a deliberate placeholder dash keeps the empty card looking intentional */}
            <span
              className="inline-block h-1 w-5 rounded-full bg-muted-foreground/25"
              aria-hidden
            />
            <p className="text-xs font-semibold leading-tight text-muted-foreground/70">
              No data available
            </p>
          </>
        ) : (
          <p className="whitespace-nowrap break-normal text-2xl font-bold tabular-nums leading-none text-foreground">
            {counted.toLocaleString()}
            {isPercent ? "%" : ""}
          </p>
        )}
      </div>
    </motion.div>
  );
}

/* â”€â”€ Pipeline â€” full widget (chevron shapes + dot timeline) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export function PipelineWidget({ stages, compact = false }: { stages: PipelineStageItem[]; compact?: boolean }) {
  /* shared hover → hovering a chevron or its dot highlights both */
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div className="w-full">
      {/* â”€â”€ Chevron shape row â”€â”€ */}
      <div className="flex items-stretch gap-0">
        {stages.map((stage, i) => (
          <PipelineHex
            key={stage.id}
            stage={stage}
            index={i}
            total={stages.length}
            isLast={i === stages.length - 1}
            compact={compact}
            hovered={hoveredId === stage.id}
            onHover={(h) => setHoveredId(h ? stage.id : null)}
          />
        ))}
      </div>

      {/* â”€â”€ Dot + line timeline row â”€â”€ */}
      <div className="relative mt-5 px-[calc(100%/8)]">
        {/* connecting line */}
        <div className="absolute inset-y-[9px] left-[calc(100%/8)] right-[calc(100%/8)] h-0.5 bg-foreground/10" />

        <div
          className="relative grid"
          style={{ gridTemplateColumns: `repeat(${stages.length}, 1fr)` }}
        >
          {stages.map((stage, i) => (
            <PipelineDot
              key={stage.id}
              stage={stage}
              index={i}
              hovered={hoveredId === stage.id}
              onHover={(h) => setHoveredId(h ? stage.id : null)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* individual pentagon/chevron card */
function PipelineHex({
  stage,
  index,
  total,
  isLast,
  compact = false,
  hovered = false,
  onHover,
}: {
  stage: (typeof PIPELINE_STAGES)[0];
  index: number;
  total: number;
  isLast: boolean;
  compact?: boolean;
  hovered?: boolean;
  onHover?: (h: boolean) => void;
}) {
  const count = useCountUp(stage.count, 900, index * 120);
  const Icon = stage.icon;

  /* Pentagon clip-path: indent on left, arrow-point on right.
     Last card: indent on left, flat on right.
     First card: flat on left, arrow-point on right. */
  const clipPath = isLast
    ? "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 12% 50%)"
    : "polygon(0% 0%, 88% 0%, 100% 50%, 88% 100%, 0% 100%, 12% 50%)";

  const firstClipPath = isLast
    ? "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"
    : "polygon(0% 0%, 88% 0%, 100% 50%, 88% 100%, 0% 100%)";

  return (
    <motion.div
      className="relative flex flex-1 min-w-0 cursor-default items-center justify-center py-5 text-center"
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
      style={{
        background: `${stage.color}40`,
        clipPath: index === 0 ? firstClipPath : clipPath,
        marginLeft: index === 0 ? 0 : "-12px",
        zIndex: hovered ? 50 : total - index,
        filter: hovered
          ? `brightness(1.12) drop-shadow(0 4px 12px ${stage.color}99)`
          : "brightness(1)",
        transition: "filter 200ms ease",
      }}
      initial={{ opacity: 0, x: -28 }}
      animate={{
        opacity: 1,
        x: 0,
        transition: { duration: 0.45, delay: index * 0.1, ease: "easeOut" },
      }}
    >
      {/* border overlay via box-shadow trick on inner wrapper */}
      <div
        className={`flex min-w-0 flex-col items-center ${compact ? "gap-0.5 px-1.5 sm:px-3" : "gap-1 px-2 sm:px-6"}`}
        style={{ filter: "brightness(1.1) saturate(1.18)" }}
      >
        {/* icon */}
        <div
          className={`mb-1 flex items-center justify-center rounded-lg ${compact ? "size-6 sm:size-7" : "size-7 sm:size-9"}`}
          style={{ background: `${stage.color}59` }}
        >
          <Icon className={compact ? "size-3.5 sm:size-4" : "size-4 sm:size-5"} style={{ color: stage.color }} />
        </div>
        {/* label */}
        <span
          className={`whitespace-nowrap font-bold tracking-wide sm:tracking-widest ${compact ? "text-[8px] sm:text-[10px]" : "text-[9px] sm:text-[11px]"}`}
          style={{ color: stage.color }}
        >
          {stage.label}
        </span>
        {/* count */}
        <span
          className={`font-extrabold leading-none tabular-nums ${compact ? "text-lg sm:text-[1.5rem]" : "text-xl sm:text-[2rem]"}`}
          style={{ color: stage.color }}
        >
          {count}
        </span>
      </div>
    </motion.div>
  );
}

/* individual dot + pct label */
function PipelineDot({
  stage,
  index,
  hovered = false,
  onHover,
}: {
  stage: (typeof PIPELINE_STAGES)[0];
  index: number;
  hovered?: boolean;
  onHover?: (h: boolean) => void;
}) {
  return (
    <motion.div
      className="relative flex cursor-default flex-col items-center gap-2"
      title={`${stage.label}: ${stage.count}`}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.45 + index * 0.08 }}
    >
      {/* dot with ring — highlights when this stage (chevron or dot) is hovered */}
      <motion.div
        className="relative z-10 size-5 rounded-full border-2 shadow-lg"
        style={{
          background: "var(--card)",
          borderColor: stage.dotColor,
        }}
        animate={{
          scale: hovered ? 1.28 : 1,
          boxShadow: hovered ? `0 0 9px ${stage.dotColor}80` : `0 0 6px ${stage.dotColor}55`,
        }}
        transition={{ type: "spring", stiffness: 400, damping: 18 }}
      >
        <div
          className="absolute inset-[3px] rounded-full"
          style={{ background: stage.dotColor }}
        />
      </motion.div>
      {/* pct — emphasises when hovered */}
      <div
        className={`flex flex-col items-center leading-tight transition-transform duration-200 ${hovered ? "scale-110" : ""}`}
      >
        <span className="text-sm font-bold text-foreground">{stage.pct}%</span>
        <span className="text-[10px] text-muted-foreground">of total</span>
      </div>
    </motion.div>
  );
}

/* â”€â”€ Findings & POA&M horizontal bar chart â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
/* Fluid widths so the row never overflows / collapses at any card size:
   the label shrinks between 120â€“210px, the count column always fits 4 digits. */
const LABEL_W = "clamp(120px, 34%, 210px)";
const COUNT_W = 48;  /* px â€” fixed count column, always fits up to 4 digits */

function buildTicks(max: number): number[] {
  /* pick a round step so we get ~4 ticks */
  const raw = max / 4;
  const mag  = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = Math.ceil(raw / mag) * mag;
  const ticks: number[] = [];
  for (let t = 0; t <= max + step; t += step) ticks.push(t);
  return ticks;
}

export function PoamChart({
  items,
}: {
  items: { label: string; count: number; color: string }[];
}) {
  const totalCount = items.reduce((sum, i) => sum + i.count, 0);
  
  // Show empty state if all counts are 0
  if (totalCount === 0) {
    return (
      <div className="flex h-32 items-center justify-center">
        <span className="text-sm text-muted-foreground">No available data</span>
      </div>
    );
  }

  const xMax  = Math.max(...items.map((i) => i.count), 1);
  const ticks = buildTicks(xMax);
  const axisMax = ticks[ticks.length - 1];

  return (
    <div className="w-full select-none">
      {/* bar rows */}
      <div className="flex flex-col gap-[14px]">
        {items.map((item, i) => (
          <PoamRow key={item.label} item={item} index={i} xMax={axisMax} />
        ))}
      </div>
    </div>
  );
}

function PoamRow({
  item,
  index,
  xMax,
}: {
  item: { label: string; count: number; color: string };
  index: number;
  xMax: number;
}) {
  const [barPct, setBarPct] = useState(0);
  const targetPct = (item.count / xMax) * 100;

  useEffect(() => {
    const t = setTimeout(() => setBarPct(targetPct), 200 + index * 80);
    return () => clearTimeout(t);
  }, [targetPct, index]);

  return (
    <motion.div
      className="flex items-center"
      initial={{ opacity: 0, x: -14 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35, delay: 0.2 + index * 0.07 }}
    >
      {/* dot + label â€” fluid width; wraps instead of overflowing when tight */}
      <div
        className="flex shrink-0 items-center gap-2 pr-3"
        style={{ width: LABEL_W }}
      >
        <div className="size-2 shrink-0 rounded-full" style={{ background: item.color }} />
        <span className="min-w-0 break-words text-[13px] font-semibold leading-tight text-foreground/90">
          {item.label}
        </span>
      </div>

      {/* bar track â€” flex-1, strictly clipped, never overflows */}
      <div className="relative min-w-0 flex-1 h-[13px] overflow-hidden rounded-sm bg-foreground/[0.06]">
        {/* animated fill */}
        <div
          className="absolute inset-y-0 left-0 rounded-sm transition-all duration-[1100ms] ease-out"
          style={{ width: `${barPct}%`, background: item.color }}
        />
      </div>

      {/* count â€” fixed column outside bar, always on one line.
         `break-normal` overrides the global word-break:break-word base rule
         (theme.css) so a multi-digit number never collapses onto two lines. */}
      <span
        className="shrink-0 break-normal pl-2.5 text-right text-sm font-bold tabular-nums whitespace-nowrap"
        style={{ minWidth: COUNT_W, color: item.color }}
      >
        {item.count}
      </span>
    </motion.div>
  );
}
